import { pickObject } from '@client/new/helpers/general';
import { toBaseSection, toCTA, toServerCTA } from '../mappers';
import {
  CTA,
  SectionBase,
  SectionConstructor,
  SectionSpec,
  ServerCTA,
} from '../domain';
import { z } from 'zod';

export type ServerIntroSection = {
  id?: string;
  cta?: ServerCTA;
  desc: string;
  type: 'Introduction';
  title: string;
  bgColor: string;
  enabled: boolean;
  subtitle?: string;
  colorIndex: number;
  coverImage?: string;
  introVideo?: string | null;
  priceBefore: null;
  primaryColor: string;
};

export type IntroSectionValue = SectionBase & {
  headlineText: string;
  subtitle: string;
  introText: string;
  coverImageUrl: string;
  introVideoUrl?: string;
  logoUrl?: string;
  primaryColor: string;
  cta: CTA;
};

export type IntroSectionSpec = SectionSpec<
  ServerIntroSection,
  IntroSectionValue
>;

const toDomain: SectionConstructor<'Introduction'>['toDomain'] = (s) => ({
  ...toBaseSection(s),
  ...pickObject(s, ['enabled', 'primaryColor']),
  headlineText: s.title,
  subtitle: s.subtitle ?? '',
  introText: s.desc,
  coverImageUrl: s.coverImage ?? '',
  introVideoUrl: s.introVideo ?? undefined,
  logoUrl: 'TODO',
  cta: toCTA(s.cta),
  type: 'Introduction',
});

const toServer: SectionConstructor<'Introduction'>['toServer'] = (s) => ({
  cta: toServerCTA(s.cta),
  desc: s.subtitle,
  type: 'Introduction',
  title: s.headlineText,
  bgColor: '',
  enabled: s.enabled,
  subtitle: s.subtitle,
  colorIndex: 0,
  coverImage: s.coverImageUrl || null,
  introVideo: s.introVideoUrl || null,
  priceBefore: null,
  primaryColor: '',
});

const schema: SectionConstructor<'Introduction'>['schema'] = z.object({
  id: z.string(),
  headlineText: z.string().min(1, 'Title required'),
  bgColor: z.string().optional(),
  enabled: z.boolean(),
  subtitle: z.string().optional(),
  introText: z.string().min(1, 'Introduction text required'),
  coverImageUrl: z.string().url().optional(),
  introVideoUrl: z.string().url().optional(),
  logoUrl: z.string().url().optional(),
  primaryColor: z.string().min(1, 'Primary color required'),
  cta: z.object({
    enabled: z.boolean(),
    text: z.string().min(1, 'CTA text required'),
    url: z.string().url('Invalid URL').optional(),
  }),
});

export const Introduction: SectionConstructor<'Introduction'> = {
  toDomain,
  toServer,
  schema,
};
